import { clsx } from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import styles from './ContextMenu.module.scss';

const VIEWPORT_PADDING = 8;

/** Single row in a `<ContextMenu>`. */
export interface ContextMenuItem {
  /** Stable identifier used as the React key. */
  id: string;
  /** Plain-text label rendered in the row. */
  label: string;
  /** When true, the row is greyed-out and clicks are suppressed. */
  disabled?: boolean;
  /** Click handler. The menu auto-closes after a click. */
  onClick: () => void;
}

/**
 * Props for `<ContextMenu>`.
 *
 * Required: `clientX`, `clientY`, `items`, `onClose`. The menu is fully
 * positioned by the consumer — typically anchored to a `contextmenu`
 * event's `clientX` / `clientY` and clamped to the viewport.
 */
export interface ContextMenuProps {
  /** Viewport-relative x to anchor the menu to (CSS pixels). */
  clientX: number;
  /** Viewport-relative y to anchor the menu to (CSS pixels). */
  clientY: number;
  /** Menu rows. */
  items: ContextMenuItem[];
  /** Called on outside-click, Escape, or after a row click. */
  onClose: () => void;
  /** Extra class name forwarded to the menu root. */
  className?: string;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Lightweight context menu rendered at viewport coordinates, with
 * automatic edge-flipping so it never clips outside the window.
 *
 * Used by chartv2 for the right-click menu on the canvas (reset chart,
 * merge / split y-axis, etc.). Closes on outside-click, Escape, or
 * after any row click — the consumer is responsible for re-mounting
 * it on each `contextmenu` event.
 */
export const ContextMenu = ({
  clientX,
  clientY,
  items,
  onClose,
  className,
  'data-testid': testId = 'chartv2-context-menu',
}: ContextMenuProps) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Default placement mirrors the original chart: the menu opens above
  // and to the left of the click (so its bottom-right anchors to the
  // cursor — natural when right-clicking near the y-axis in the top
  // corner of a pane). Clamped to the viewport so it never clips.
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: clientY,
    left: clientX,
  });

  useLayoutEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return;
    }

    const { offsetWidth: w, offsetHeight: h } = node;
    const { innerWidth: vw, innerHeight: vh } = window;

    // Place menu so its bottom-right corner sits at the click point…
    let top = clientY - h;
    let left = clientX - w;

    // …then clamp to the viewport. If the menu would overflow the top
    // edge (click near the top of the viewport), flip it below the
    // click. Same logic for the left edge.
    if (top < VIEWPORT_PADDING) {
      top = Math.min(clientY, vh - h - VIEWPORT_PADDING);
    }

    if (left < VIEWPORT_PADDING) {
      left = Math.min(clientX, vw - w - VIEWPORT_PADDING);
    }

    top = Math.max(VIEWPORT_PADDING, Math.min(top, vh - h - VIEWPORT_PADDING));
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, vw - w - VIEWPORT_PADDING)
    );

    setPosition({ top, left });
  }, [clientX, clientY, items.length]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={clsx(styles.root, className)}
      style={{ top: position.top, left: position.left }}
      role="menu"
      data-testid={testId}
    >
      {items.map(item => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={clsx(styles.item, item.disabled && styles.disabled)}
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled) {
              return;
            }

            item.onClick();
            onClose();
          }}
          data-testid={`${testId}-${item.id}`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
